/**
 * PDP Nutrition Modal
 * Uses native <dialog> element with JS fallbacks for focus + backdrop dismiss.
 *
 * Open path (as of 2026-04-17): the standalone trigger section was removed
 * from the PDP template. The dialog is opened ONLY by the inline "Nutrition
 * Facts & Ingredients →" link injected into the product description by
 * pdp-hero.js initDescriptionLink(). That code dispatches a
 * 'pdp-nutrition:open' CustomEvent on the dialog with detail.opener = link.
 *
 * If a legacy trigger button (data-pdp-nutrition-open) still exists in the
 * DOM — e.g. a future template re-introduces the standalone section — the
 * init wires it too, for backward compatibility.
 */
(function () {
  'use strict';

  function init(root) {
    // Re-entrancy guard. Shop All renders one .pdp-nutrition root per card,
    // and shop-all.js calls boot() again after paginate-merge appends more
    // cards to the grid. Without this, the listeners on already-inited
    // dialogs get attached a second time (double-fires on every click).
    if (root.dataset.pdpNutritionInited === '1') return;
    root.dataset.pdpNutritionInited = '1';

    const dialog = root.querySelector('[data-pdp-nutrition-dialog]');
    if (!dialog) return;

    const openBtn = root.querySelector('[data-pdp-nutrition-open]'); // optional
    const closeBtn = root.querySelector('[data-pdp-nutrition-close]');
    const supportsDialog = typeof dialog.showModal === 'function';

    // Tracks who opened the dialog, so we return focus there on close.
    // Default to the legacy openBtn if one exists.
    let lastOpener = openBtn || null;

    function open() {
      if (supportsDialog) {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.classList.add('is-fallback-open');
      }
      document.body.classList.add('pdp-nutrition-locked');
      // trap focus on the close button initially
      requestAnimationFrame(function () {
        if (closeBtn) closeBtn.focus();
      });
    }

    function close() {
      if (supportsDialog && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        dialog.classList.remove('is-fallback-open');
      }
      document.body.classList.remove('pdp-nutrition-locked');
      // Return focus to whoever opened the dialog (a11y).
      if (lastOpener && typeof lastOpener.focus === 'function') {
        lastOpener.focus();
      }
    }

    // Primary open path: CustomEvent from the inline description link.
    dialog.addEventListener('pdp-nutrition:open', function (e) {
      if (e && e.detail && e.detail.opener) {
        lastOpener = e.detail.opener;
      }
      open();
    });

    // Legacy / backward-compat: if a visible trigger button is present
    // (old standalone section), still wire it.
    if (openBtn) {
      openBtn.addEventListener('click', function () {
        lastOpener = openBtn;
        open();
      });
    }

    if (closeBtn) closeBtn.addEventListener('click', close);

    // Click on backdrop (outside dialog-inner) closes
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) {
        close();
      }
    });

    // ESC handler for fallback (native <dialog> handles ESC natively)
    dialog.addEventListener('cancel', function () {
      document.body.classList.remove('pdp-nutrition-locked');
      // `cancel` fires before the dialog closes — still return focus after.
      if (lastOpener && typeof lastOpener.focus === 'function') {
        requestAnimationFrame(function () { lastOpener.focus(); });
      }
    });
    if (!supportsDialog) {
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && dialog.hasAttribute('open')) close();
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-pdp-nutrition]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose so other sections (Shop All) can re-boot after dynamically
  // appending more .pdp-nutrition roots to the DOM. Safe to call repeatedly —
  // init() guards via the data-pdp-nutrition-inited flag.
  window.pdpNutritionBoot = boot;
})();
